import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal, WritableSignal } from '@angular/core';
import { CommonModule, AsyncPipe } from '@angular/common';
import { GalaxyTableModule } from '@vendasta/galaxy/table';
import { GalaxyColumnDef } from '@vendasta/galaxy/table/src/table.interface';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { TranslateModule } from '@ngx-translate/core';
import { MatCardModule } from '@angular/material/card';
import {
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CardComponent, BlankCardComponent, DefaultCardAction } from '@galaxy/email-ui/email-library';
import { EmailTemplateRow, EmailTemplateService } from '../email-template.service';
import { GalaxyDataSource } from '@vendasta/galaxy/table';
import { MatButton } from '@angular/material/button';
import { BreakpointObserver } from '@angular/cdk/layout';
import { BREAKPOINTS_PX } from '@vendasta/galaxy/constants';
import { map } from 'rxjs';
import { LanguageService } from '../../../core/language-service.service';
import {
  GalaxyFilterChipsModule,
  GalaxyFilterChipInjectionToken,
  GalaxyFilterInterface,
} from '@vendasta/galaxy/filter/chips';
import { EmailTemplateFilterService } from '../email-template-filter.service';

@Component({
  selector: 'app-email-select-template',

  imports: [
    CommonModule,
    GalaxyTableModule,
    ScrollingModule,
    TranslateModule,
    MatCardModule,
    AsyncPipe,
    CardComponent,
    BlankCardComponent,
    MatDialogTitle,
    MatDialogContent,
    MatButton,
    MatDialogClose,
    MatDialogActions,
    GalaxyFilterChipsModule,
  ],
  providers: [
    {
      provide: GalaxyFilterChipInjectionToken,
      useExisting: EmailTemplateFilterService,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './select-template.component.html',
  styleUrl: './select-template.component.scss',
})
export class SelectTemplateComponent {
  private readonly templateService = inject(EmailTemplateService);
  private readonly dialogRef = inject(MatDialogRef<SelectTemplateComponent>);
  private readonly destroyRef = inject(DestroyRef);
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly languageService = inject(LanguageService);

  protected readonly dataSource: GalaxyDataSource<EmailTemplateRow, GalaxyFilterInterface> =
    this.templateService.getDataSource();
  protected readonly columns$: Observable<GalaxyColumnDef[]> = this.templateService.getColumns$();
  protected readonly displayOption: WritableSignal<'grid' | 'list'> = signal('grid');
  protected readonly filters: WritableSignal<GalaxyFilterInterface[]> = signal([]);
  protected readonly DefaultCardAction = DefaultCardAction;
  protected readonly isMobile$ = this.breakpointObserver
    .observe([`(max-width: ${BREAKPOINTS_PX.TABLET_MINIMUM})`])
    .pipe(map((result) => result.matches));
  protected readonly currentLocale$ = this.languageService.currentLocale$;
  protected readonly dateOptions: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  };

  constructor() {
    if (this.dataSource) {
      this.dataSource.state = { ...this.dataSource.state, shouldRefetch: true };
      this.dataSource.connect().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    }
  }

  protected formatDate(date: Date, locale: string): string {
    return date.toLocaleDateString(locale, this.dateOptions);
  }

  protected handleFiltersChanged(filters: GalaxyFilterInterface[]): void {
    this.filters.set(filters);
    this.dataSource.setFilters(filters);
  }

  templateSelected(row: EmailTemplateRow): void {
    this.dialogRef.close({
      id: row.template.templateId,
      name: row.template.title,
      ownerType: row.ownerType,
      ownerId: row.ownerId,
    });
  }

  blankTemplateSelected(): void {
    this.dialogRef.close({ id: null, name: null, ownerType: null, ownerId: null });
  }
}
